import { checkLogoDataUrl } from '../../supabase/functions/_shared/logo-image-data';
import { makeLogoTransparent } from '@/lib/logo-background';

export interface ProcessedLogo {
  /** Transparent, trimmed PNG as a data URL, for <img> previews. */
  dataUrl: string;
  /** The same PNG, for upload. */
  png: Blob;
  width: number;
  height: number;
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Your browser couldn’t save the logo.'))), 'image/png');
  });
}

/**
 * A picture logo from ai-generate-logo (a data URL on white) → a transparent,
 * trimmed PNG. The data URL is checked again first (PNG/JPEG/WebP only, size
 * capped) and only ever drawn through an <img> and a canvas: never injected as
 * markup. If no logo is found on the white, the original is kept as it is.
 */
export async function processLogoImage(source: string): Promise<ProcessedLogo> {
  const checked = checkLogoDataUrl(source);
  if (!checked.ok) throw new Error('That logo couldn’t be prepared.');

  const image = new Image();
  image.decoding = 'async';
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error('That logo couldn’t be drawn.'));
    image.src = source;
  });

  const width = image.naturalWidth;
  const height = image.naturalHeight;
  if (!width || !height) throw new Error('That logo couldn’t be drawn.');
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Your browser couldn’t draw the logo.');
  ctx.drawImage(image, 0, 0);

  const { pixels } = makeLogoTransparent(ctx.getImageData(0, 0, width, height));
  canvas.width = pixels.width;
  canvas.height = pixels.height;
  ctx.putImageData(new ImageData(pixels.data, pixels.width, pixels.height), 0, 0);

  return { dataUrl: canvas.toDataURL('image/png'), png: await canvasToBlob(canvas), width: pixels.width, height: pixels.height };
}
